import React, { useState, useCallback, useEffect } from 'react'
import {
  PoolListAccordionContainer,
  PoolListAccordionListItem,
  PoolListAccordionButton,
  PoolListAccordionDetailsList,
  ShowMorePoolsButton,
} from './styled'
import PoolListCard from '../PoolListCard'

const PoolList: React.FC<{ pools: any; token0: any; token1: any; rewards: any }> = ({
  pools = [],
  token0,
  token1,
  rewards,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [showMorePools, setShowMorePools] = useState(false)
  const [displayedPools, setDisplayedPools] = useState<any[]>([])

  useEffect(() => {
    if (!isOpen) {
      setShowMorePools(false)
    }
  }, [isOpen])

  const getTopPools = useCallback((pools: any[]): { bestAprPool: any; bestVolumePool: any; bestTvlPool: any } => {
    if (!pools.length) return { bestAprPool: null, bestVolumePool: null, bestTvlPool: null }

    const sortedByApr = [...pools].sort((a, b) => b.apr - a.apr)
    const sortedByVolume = [...pools].sort((a, b) => b.h24 - a.h24)
    const sortedByTvl = [...pools].sort((a, b) => b.tvl - a.tvl)

    const bestAprPool = sortedByApr[0]
    const bestVolumePool = sortedByVolume[0]
    const bestTvlPool = sortedByTvl[0]

    return {
      bestAprPool,
      bestVolumePool,
      bestTvlPool,
    }
  }, [])

  useEffect(() => {
    if (pools && pools.length > 0) {
      const { bestAprPool, bestVolumePool, bestTvlPool } = getTopPools(pools)
      const uniquePools = new Set([bestAprPool, bestVolumePool, bestTvlPool].filter(Boolean))
      setDisplayedPools(Array.from(uniquePools))
    }
  }, [pools, getTopPools])

  if (pools.length === 0) return null
  const pairName = `${token0.token0Symbol === 'WHYPE' ? 'HYPE' : token0.token0Symbol} / 
  ${token1.token1Symbol === 'WHYPE' ? 'HYPE' : token1.token1Symbol}`

  const filteredPools = pools
    .filter((pool: any) => !displayedPools.some((displayedPool) => displayedPool.pairAddress === pool.pairAddress))
    .sort((a: any, b: any) => {
      const tvlA = Number(a.tvl)
      const tvlB = Number(b.tvl)
      return tvlB - tvlA
    })

  return (
    <PoolListAccordionContainer>
      <PoolListAccordionListItem onClick={() => setIsOpen(!isOpen)}>
        <div className="title pool-display-none-mobile">
          <h3>Pools ({pools.length})</h3>
        </div>
        <div className="wrapper-infos pool-display-none-mobile">
          <span className="apr">{isOpen && 'APR'} </span>
          <span className="daily-fees">{isOpen && 'Daily Fees'}</span>
          <span className="tvl">{isOpen && 'TVL'}</span>
          <span className="volume">{isOpen && '1D Volume'}</span>
          <span className="reserve">{isOpen && 'Reserve'}</span>
          <PoolListAccordionButton onClick={() => setIsOpen(!isOpen)} className="btn">
            {isOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M6 15l6 -6l6 6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M6 9l6 6l6 -6" />
              </svg>
            )}
          </PoolListAccordionButton>
        </div>

        <div
          className="pool-display-none-desktop"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '0 8px',
          }}
        >
          <h3>Pools ({pools.length})</h3>
          <PoolListAccordionButton onClick={() => setIsOpen(!isOpen)} className="btn" style={{ paddingRight: '0' }}>
            {isOpen ? (
              <svg
                style={{ marginRight: '0' }}
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M6 15l6 -6l6 6" />
              </svg>
            ) : (
              <svg
                style={{ marginRight: '0' }}
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M6 9l6 6l6 -6" />
              </svg>
            )}
          </PoolListAccordionButton>
        </div>
      </PoolListAccordionListItem>
      <PoolListAccordionDetailsList isOpen={isOpen}>
        {displayedPools.map((pool: any) => {
          const { bestAprPool, bestVolumePool, bestTvlPool } = getTopPools(pools)
          const isBestApr = pool.pairAddress === bestAprPool?.pairAddress
          const isBestVolume = pool.pairAddress === bestVolumePool?.pairAddress
          const isBestTvl = pool.pairAddress === bestTvlPool?.pairAddress

          return (
            <PoolListCard
              key={pool.pairAddress}
              pool={pool}
              token0={token0}
              token1={token1}
              pairName={pairName}
              isBestApr={isBestApr}
              isBestVolume={isBestVolume}
              isBestTvl={isBestTvl}
              rewards={rewards}
            />
          )
        })}
        {showMorePools && <span></span>}
      </PoolListAccordionDetailsList>
      <PoolListAccordionDetailsList isOpen={showMorePools}>
        {filteredPools.map((pool: any) => (
          <PoolListCard
            key={pool.pairAddress}
            pool={pool}
            token0={token0}
            token1={token1}
            pairName={pairName}
            rewards={rewards}
          />
        ))}
      </PoolListAccordionDetailsList>
      {filteredPools.length > 0 && isOpen && (
        <ShowMorePoolsButton onClick={() => setShowMorePools(!showMorePools)}>
          {showMorePools ? 'Show less' : 'Show more'}
          {!showMorePools ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M6 9l6 6l6 -6" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-up"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M6 15l6 -6l6 6" />
            </svg>
          )}
        </ShowMorePoolsButton>
      )}
    </PoolListAccordionContainer>
  )
}
export default PoolList
